'use client';

import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { resolvePopoverPosition, type PopoverPlacement } from './popover-position';

export interface AnchoredPosition {
    top: number;
    left: number;
    placement: PopoverPlacement;
    /** Where the arrow sits along the box, in px; undefined until first measured (the arrow starts centered). */
    arrowOffset: number | undefined;
}

/**
 * Keeps an open popover placed against its anchor. Each frame it compares
 * the anchor's box, the popover's own size and the viewport, and places it
 * again only when one of them changed: so it follows a frame that slides or
 * animates, and a popover whose content grows never spills over the anchor
 * it describes (which would steal the anchor's hover and make it flicker).
 */
export function useAnchoredPosition(
    open: boolean,
    getAnchor: () => HTMLElement | null | undefined,
    popoverRef: RefObject<HTMLElement>,
    preferred: PopoverPlacement,
): AnchoredPosition {
    const [position, setPosition] = useState<AnchoredPosition>({ top: 0, left: 0, placement: preferred, arrowOffset: undefined });
    // Read through refs, so a caller passing a new function or ref object each render never restarts the loop.
    const getAnchorRef = useRef(getAnchor);
    getAnchorRef.current = getAnchor;
    const popoverRefRef = useRef(popoverRef);
    popoverRefRef.current = popoverRef;

    useLayoutEffect(() => {
        if (!open) {
            return undefined;
        }
        let lastKey = '';
        let frame = 0;
        const follow = (): void => {
            const anchor = getAnchorRef.current();
            const popover = popoverRefRef.current.current;
            if (anchor && popover) {
                const anchorRect = anchor.getBoundingClientRect();
                const popoverRect = popover.getBoundingClientRect();
                const key = [
                    anchorRect.left, anchorRect.top, anchorRect.width, anchorRect.height,
                    popoverRect.width, popoverRect.height, window.innerWidth, window.innerHeight,
                ].join(',');
                if (key !== lastKey) {
                    lastKey = key;
                    const next = resolvePopoverPosition(anchorRect, popoverRect, preferred, { width: window.innerWidth, height: window.innerHeight });
                    // Unchanged placement keeps the same state, so nothing re-renders.
                    setPosition((current) => (
                        current.top === next.top && current.left === next.left && current.placement === next.placement && current.arrowOffset === next.arrowOffset
                            ? current
                            : { top: next.top, left: next.left, placement: next.placement, arrowOffset: next.arrowOffset }
                    ));
                }
            }
            frame = requestAnimationFrame(follow);
        };
        // The first placement runs now, before the popover is painted, so it
        // never shows for a frame wherever the last one was (over its own
        // trigger, say, taking the hover and closing it again).
        follow();
        return (): void => cancelAnimationFrame(frame);
    }, [open, preferred]);

    return position;
}
